import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Bike, LayoutDashboard, Users, Compass, Plus, Trash2, Pencil,
  ArrowLeft, LogOut, RotateCcw, TrendingUp, QrCode, KeyRound, Copy, Printer, Upload, BarChart3, Mic, LifeBuoy, MapPin, Bell, BellOff, CalendarCheck, BookOpen, Link2, Phone, ShieldCheck,
} from "lucide-react";
import { AdminBookings } from "@/components/AdminBookings";
import { StaffGuide } from "@/components/StaffGuide";
import { SecurityPanel } from "@/components/SecurityPanel";
import { RidersOnRoad } from "@/components/RidersOnRoad";
import { MapsEditor } from "@/components/MapsEditor";
import { AttachCodeDialog } from "@/components/AttachCodeDialog";
import { QRCodes } from "@/components/QRCodes";
import { StaffLogin } from "@/components/StaffLogin";
import { staffMe, staffLogout } from "@/lib/api";
import {
  getStats, getBikes, createBike, updateBike, deleteBike,
  getHires, returnHire, deleteHire,
  getSelfGuidedRoutes, createSelfGuidedRoute, deleteSelfGuidedRoute,
  listCodes, generateCodes, deleteCode, getActivations, getLostAlerts, resolveLost,
} from "@/lib/api";
import { printCodeCards } from "@/lib/print";
import { VoiceoverManager } from "@/components/VoiceoverManager";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { StatusBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
  { id: "bikes", label: "Bikes", icon: Bike },
  { id: "hires", label: "Hires", icon: Users },
  { id: "self-guided", label: "Self-Guided", icon: Compass },
  { id: "maps", label: "Maps", icon: MapPin },
  { id: "qr", label: "QR codes", icon: QrCode },
  { id: "guide", label: "Staff guide", icon: BookOpen },
  { id: "security", label: "Security", icon: ShieldCheck },
];

const DIFFICULTY = ["Easy", "Moderate", "Hard", "Expert"];

const emptyBike = { name: "", price_per_hour: 25, total_qty: 5, image: "", description: "" };
const emptyStop = { name: "", lat: "", lng: "", story: "", imagesText: "" };
const emptySG = { title: "", unlock_code: "", description: "", price: 25, distance_km: 10, duration_text: "", difficulty: "Easy", cover_image: "", stops: [{ ...emptyStop }], path: [] };

const Metric = ({ label, value, sub }) => (
  <div className="border border-white/10 rounded-xl p-6 bg-[#001A4D]/20">
    <p className="overline text-[#F5F5F0]/40">{label}</p>
    <p className="font-serif text-4xl text-[#F5F5F0] mt-3">{value}</p>
    {sub && <p className="text-xs text-[#F5F5F0]/40 mt-1 font-mono">{sub}</p>}
  </div>
);

function AdminInner({ user, onLogout }) {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [bikes, setBikes] = useState([]);
  const [hires, setHires] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [activations, setActivations] = useState([]);
  const [lost, setLost] = useState([]);
  const [attach, setAttach] = useState(null);
  const [flash, setFlash] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("wc_alert_sound") !== "off");
  const seenRef = useRef(null);
  const audioCtxRef = useRef(null);
  const chime = () => {
    try {
      const ctx = audioCtxRef.current || (audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)());
      if (ctx.state === "suspended") ctx.resume();
      [0, 0.18, 0.36].forEach((t, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "sine"; o.frequency.value = [880, 1108, 1320][i];
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.5);
        o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.55);
      });
    } catch { /* audio unavailable */ }
  };
  const toggleSound = () => {
    const next = !soundOn; setSoundOn(next); localStorage.setItem("wc_alert_sound", next ? "on" : "off");
    if (next) chime();
  };
  const loadLost = () => getLostAlerts().then((list) => {
    setLost(list);
    if (seenRef.current) {
      const fresh = list.filter((a) => !seenRef.current.has(a.id));
      if (fresh.length) {
        setFlash(true); setTimeout(() => setFlash(false), 12000);
        if (soundOn) chime();
        fresh.forEach((a) => toast.error(`Lost rider on ${a.route_title} near ${a.stop_name || `stop ${a.stop_index + 1}`}`, { duration: 15000 }));
        document.title = `(${list.length}) Lost rider! · Admin`;
      }
    }
    seenRef.current = new Set(list.map((a) => a.id));
    if (list.length === 0) document.title = "Admin · Whangamatā Cycles";
  }).catch(() => {});
  useEffect(() => { loadLost(); const id = setInterval(loadLost, 10000); return () => clearInterval(id); }, [soundOn]); // eslint-disable-line react-hooks/exhaustive-deps
  const doResolve = async (id) => {
    try { await resolveLost(id); toast.success("Marked as found"); loadLost(); } catch { toast.error("Could not update"); }
  };

  const [bikeDialog, setBikeDialog] = useState(false);
  const [bikeForm, setBikeForm] = useState(emptyBike);
  const [editBikeId, setEditBikeId] = useState(null);

  const [sgDialog, setSgDialog] = useState(false);
  const [sgForm, setSgForm] = useState(emptySG);

  const [codesDialog, setCodesDialog] = useState(false);
  const [codesRoute, setCodesRoute] = useState(null);
  const [codesList, setCodesList] = useState([]);
  const [genCount, setGenCount] = useState(5);
  const [genLoading, setGenLoading] = useState(false);

  const [voDialog, setVoDialog] = useState(false);
  const [voRoute, setVoRoute] = useState(null);

  const loadAll = () => {
    getStats().then(setStats).catch(() => {});
    getBikes().then(setBikes).catch(() => {});
    getHires().then(setHires).catch(() => {});
    getSelfGuidedRoutes().then(setRoutes).catch(() => {});
    getActivations(14).then(setActivations).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  // Bikes
  const openNewBike = () => { setBikeForm(emptyBike); setEditBikeId(null); setBikeDialog(true); };
  const openEditBike = (b) => { setBikeForm(b); setEditBikeId(b.id); setBikeDialog(true); };
  const saveBike = async () => {
    if (!bikeForm.name) return toast.error("Name is required");
    try {
      const payload = { name: bikeForm.name, price_per_hour: Number(bikeForm.price_per_hour), total_qty: Number(bikeForm.total_qty), image: bikeForm.image, description: bikeForm.description };
      if (editBikeId) await updateBike(editBikeId, payload);
      else await createBike(payload);
      toast.success(editBikeId ? "Bike updated" : "Bike added");
      setBikeDialog(false); loadAll();
    } catch { toast.error("Could not save bike"); }
  };
  const removeBike = async (id) => {
    try { await deleteBike(id); toast.success("Bike removed"); loadAll(); }
    catch { toast.error("Could not remove bike"); }
  };

  // Hires
  const doReturn = async (id) => {
    try { await returnHire(id); toast.success("Bike returned"); loadAll(); }
    catch { toast.error("Could not process return"); }
  };
  const removeHire = async (id) => {
    try { await deleteHire(id); toast.success("Hire deleted"); loadAll(); }
    catch { toast.error("Could not delete hire"); }
  };

  // Self-guided
  const openNewSG = () => { setSgForm({ ...emptySG, stops: [{ ...emptyStop }] }); setSgDialog(true); };
  const setStop = (i, field, val) => {
    setSgForm((f) => ({ ...f, stops: f.stops.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));
  };
  const addStop = () => setSgForm((f) => ({ ...f, stops: [...f.stops, { ...emptyStop }] }));
  const removeStop = (i) => setSgForm((f) => ({ ...f, stops: f.stops.filter((_, idx) => idx !== i) }));
  const saveSG = async () => {
    if (!sgForm.title || !sgForm.unlock_code) return toast.error("Title and unlock code are required");
    const validStops = sgForm.stops.filter((s) => s.name && s.lat && s.lng);
    if (validStops.length === 0) return toast.error("Add at least one stop with a name and coordinates");
    try {
      await createSelfGuidedRoute({
        ...sgForm,
        price: Number(sgForm.price) || 0,
        distance_km: Number(sgForm.distance_km) || 0,
        path: Array.isArray(sgForm.path) ? sgForm.path : [],
        stops: validStops.map((s) => {
          const images = (s.imagesText || "").split(/\n+/).map((x) => x.trim()).filter(Boolean);
          return { name: s.name, story: s.story, lat: Number(s.lat), lng: Number(s.lng), image: images[0] || "", images };
        }),
      });
      toast.success("Route created");
      setSgDialog(false); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save route"); }
  };
  const removeSG = async (id) => {
    try { await deleteSelfGuidedRoute(id); toast.success("Route removed"); loadAll(); }
    catch { toast.error("Could not remove route"); }
  };
  const copyCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code ${code} copied`);
    } catch {
      toast.info(`Copy this code: ${code}`);
    }
  };

  // Access codes
  const openCodes = async (route) => {
    setCodesRoute(route); setCodesList([]); setCodesDialog(true);
    try { setCodesList(await listCodes(route.id)); } catch { toast.error("Could not load codes"); }
  };
  const refreshCodes = async () => {
    if (!codesRoute) return;
    try { setCodesList(await listCodes(codesRoute.id)); } catch { /* ignore */ }
  };
  const doGenerate = async () => {
    if (!codesRoute) return;
    setGenLoading(true);
    try {
      const r = await generateCodes(codesRoute.id, Number(genCount) || 1);
      toast.success(`Generated ${r.codes.length} code${r.codes.length === 1 ? "" : "s"}`);
      await refreshCodes();
    } catch { toast.error("Could not generate codes"); }
    finally { setGenLoading(false); }
  };
  const removeCode = async (id) => {
    try { await deleteCode(id); await refreshCodes(); toast.success("Code deleted"); }
    catch { toast.error("Could not delete code"); }
  };
  const printCards = async (codes) => {
    if (!codes.length) return toast.error("No codes to print");
    const ok = await printCodeCards(codesRoute?.title || "Self-Guided Map", codes);
    if (!ok) toast.error("Please allow pop-ups to print the cards");
  };

  // KML import (Google Earth) -> prefill route stops
  const importKml = async (file) => {
    try {
      const text = await file.text();
      const xml = new DOMParser().parseFromString(text, "application/xml");
      const pms = Array.from(xml.getElementsByTagName("Placemark"));
      const stops = [];
      const path = [];
      pms.forEach((pm) => {
        const line = pm.getElementsByTagName("LineString")[0];
        if (line) {
          const lc = line.getElementsByTagName("coordinates")[0]?.textContent?.trim();
          if (lc) {
            lc.split(/\s+/).forEach((tuple) => {
              const p = tuple.split(",").map(Number);
              if (!Number.isNaN(p[1]) && !Number.isNaN(p[0])) path.push([p[1], p[0]]);
            });
          }
          return;
        }
        const point = pm.getElementsByTagName("Point")[0];
        if (!point) return;
        const coords = point.getElementsByTagName("coordinates")[0]?.textContent?.trim();
        if (!coords) return;
        const parts = coords.split(/[\s,]+/).map(Number);
        const lng = parts[0], lat = parts[1];
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        const name = pm.getElementsByTagName("name")[0]?.textContent?.trim() || "Stop";
        let desc = pm.getElementsByTagName("description")[0]?.textContent || "";
        desc = desc.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        stops.push({ name, lat, lng, story: desc, imagesText: "" });
      });
      if (stops.length === 0) return toast.error("No map pins (Placemarks with a point) found in that KML");
      const docName = xml.getElementsByTagName("Document")[0]?.getElementsByTagName("name")[0]?.textContent?.trim();
      let dist = 0;
      for (let i = 0; i < path.length - 1; i++) {
        const a = path[i], b = path[i + 1];
        const dLat = (b[0] - a[0]) * Math.PI / 180;
        const dLon = (b[1] - a[1]) * Math.PI / 180;
        const x = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
        dist += 2 * 6371 * Math.asin(Math.sqrt(x));
      }
      setSgForm((f) => ({ ...f, title: f.title || docName || "", stops, path, distance_km: dist ? Math.round(dist * 10) / 10 : f.distance_km }));
      toast.success(`Imported ${stops.length} stop${stops.length === 1 ? "" : "s"}${path.length ? ` and a ${path.length}-point route line` : ""} from KML`);
    } catch {
      toast.error("Could not read that KML file");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] grain">
      <header className="border-b border-white/10 sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/80">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-[#001A4D] flex items-center justify-center">
              <Bike className="w-5 h-5" strokeWidth={1.5} />
            </span>
            <div>
              <p className="font-serif text-xl leading-none">Whangamatā Cycles</p>
              <p className="overline text-[#F5F5F0]/40">Admin Console</p>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-2 text-sm text-[#F5F5F0]/60 hover:text-white transition-colors"><LogOut className="w-4 h-4" /> Sign out</button>
          <Link to="/" data-testid="admin-back-site" className="flex items-center gap-2 text-sm text-[#F5F5F0]/60 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                  tab === t.id ? "bg-[#001A4D] text-white" : "text-[#F5F5F0]/60 border border-white/10 hover:border-white/30"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Overview */}
        {tab === "overview" && stats && (
          <div className="space-y-6" data-testid="admin-overview">
            <div className={`border rounded-xl p-6 transition-colors ${flash ? "alert-flash" : lost.length ? "border-[#93C5FD]/60 bg-[#2563EB]/10" : "border-white/10"}`} data-testid="lost-riders-panel" data-flashing={flash ? "true" : "false"}>
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-[#93C5FD]" /><p className="overline text-[#F5F5F0]/70">Lost riders · {lost.length} open</p></div>
                <button data-testid="alert-sound-toggle" onClick={toggleSound} className={`h-8 px-3 rounded-full text-xs whitespace-nowrap flex items-center gap-1.5 border ${soundOn ? "border-[#93C5FD]/50 text-[#93C5FD]" : "border-white/15 text-white/50"}`}>
                  {soundOn ? <><Bell className="w-3.5 h-3.5" /> Chime on</> : <><BellOff className="w-3.5 h-3.5" /> Chime off</>}
                </button>
              </div>
              {lost.length === 0 && <p className="text-sm text-[#F5F5F0]/40">No one needs help right now.</p>}
              {lost.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-white/10 last:border-0" data-testid={`lost-alert-${a.id}`}>
                  <div>
                    <p className="text-sm font-medium">{a.route_title} · near stop {a.stop_index + 1}{a.stop_name ? ` (${a.stop_name})` : ""}</p>
                    <p className="text-sm text-[#93C5FD]" data-testid={`lost-rider-${a.id}`}>{a.rider_name ? <>{a.rider_name} · <a href={`tel:${a.rider_phone}`} className="underline font-mono">{a.rider_phone || "no phone"}</a> · {a.booking_ref}</> : "Walk-in / shared code — no booking details"}</p>
                    <p className="text-xs text-[#F5F5F0]/50 font-mono">{new Date(a.created_at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })} · {a.lat != null ? `${a.lat.toFixed(5)}, ${a.lng.toFixed(5)}` : "no GPS fix"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.rider_phone && <a data-testid={`lost-call-${a.id}`} href={`tel:${a.rider_phone}`} className="h-9 px-3 rounded-full bg-[#93C5FD] text-[#001A4D] text-xs flex items-center gap-1.5 font-medium"><Phone className="w-3.5 h-3.5" /> Call</a>}
                    {a.lat != null && (
                      <a data-testid={`lost-open-map-${a.id}`} href={`https://www.google.com/maps?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer" className="h-9 px-3 rounded-full border border-white/20 text-xs flex items-center gap-1.5 hover:bg-white/10"><MapPin className="w-3.5 h-3.5" /> Open location</a>
                    )}
                    <Button data-testid={`lost-resolve-${a.id}`} size="sm" onClick={() => doResolve(a.id)} className="rounded-full bg-[#F5F5F0] text-[#001A4D] hover:bg-white h-9">Found them</Button>
                  </div>
                </div>
              ))}
            </div>
            <RidersOnRoad />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Metric label="Available Now" value={stats.available_units} sub={`of ${stats.total_units} total units`} />
              <Metric label="Active Hires" value={stats.active_hires} sub={`${stats.bike_types} bike types`} />
              <Metric label="Self-Guided Routes" value={stats.self_guided_routes} sub="live for customers" />
              <Metric label="Hire Revenue" value={`$${stats.hire_revenue}`} sub="active + returned" />
            </div>
            <div className="border border-white/10 rounded-xl p-6" data-testid="activations-chart">
              <div className="flex items-center gap-2 mb-4"><BarChart3 className="w-4 h-4 text-[#F5F5F0]/50" /><p className="overline text-[#F5F5F0]/50">Map Codes Activated · last 14 days · {activations.reduce((a, b) => a + b.count, 0)} total</p></div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={activations} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#F5F5F0", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fill: "#F5F5F0", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#F5F5F0" }} labelStyle={{ color: "#F5F5F0" }} />
                  <Bar dataKey="count" name="Activated" fill="#4C6EF5" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-[#F5F5F0]/50" /><p className="overline text-[#F5F5F0]/50">Recent Hires</p></div>
              {hires.slice(0, 6).map((h) => (
                <div key={h.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                  <div><p className="text-sm">{h.customer_name}</p><p className="text-xs text-[#F5F5F0]/40 font-mono">{h.bike_name} · {h.hours}h · {h.hire_date}</p></div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-[#F5F5F0]/70">${h.total_price}</span>
                    <StatusBadge status={h.status} />
                  </div>
                </div>
              ))}
              {hires.length === 0 && <p className="text-sm text-[#F5F5F0]/40">No hires yet.</p>}
            </div>
          </div>
        )}

        {/* Bikes */}
        {tab === "bikes" && (
          <div data-testid="admin-bikes">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl">Fleet Management</h2>
              <Button data-testid="add-bike-btn" onClick={openNewBike} className="rounded-full bg-[#001A4D] hover:bg-[#00287A]"><Plus className="w-4 h-4 mr-1" /> Add bike</Button>
            </div>
            <div className="border border-white/10 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/10 text-left overline text-[#F5F5F0]/40">
                  <th className="p-4">Bike</th><th className="p-4">$/hour</th><th className="p-4">Total</th><th className="p-4">Available</th><th className="p-4 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {bikes.map((b) => (
                    <tr key={b.id} className="border-b border-white/5 last:border-0" data-testid={`admin-bike-row-${b.id}`}>
                      <td className="p-4 flex items-center gap-3">
                        {b.image ? <img src={b.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-white/10" /> : <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center"><Bike className="w-5 h-5 text-white/30" /></div>}
                        <span className="font-medium">{b.name}</span>
                      </td>
                      <td className="p-4 font-mono">${b.price_per_hour}</td>
                      <td className="p-4 font-mono text-[#F5F5F0]/70">{b.total_qty}</td>
                      <td className="p-4 font-mono text-[#F5F5F0]/70">{b.available_qty}</td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button data-testid={`edit-bike-${b.id}`} onClick={() => openEditBike(b)} className="p-2 hover:text-white text-[#F5F5F0]/50"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`delete-bike-${b.id}`} onClick={() => removeBike(b.id)} className="p-2 hover:text-red-400 text-[#F5F5F0]/50"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Hires */}
        {tab === "hires" && (
          <div data-testid="admin-hires">
            <h2 className="font-serif text-3xl mb-6">Hires</h2>
            <div className="border border-white/10 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/10 text-left overline text-[#F5F5F0]/40">
                  <th className="p-4">Customer</th><th className="p-4">Bike</th><th className="p-4">Date · Hours</th><th className="p-4">Total</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {hires.map((h) => (
                    <tr key={h.id} className="border-b border-white/5 last:border-0" data-testid={`admin-hire-row-${h.id}`}>
                      <td className="p-4"><p className="font-medium">{h.customer_name}</p><p className="text-xs text-[#F5F5F0]/40">{h.customer_email}</p></td>
                      <td className="p-4 text-[#F5F5F0]/70">{h.bike_name}</td>
                      <td className="p-4 font-mono text-xs text-[#F5F5F0]/70">{h.hire_date}<br />{h.hours} hour(s)</td>
                      <td className="p-4 font-mono">${h.total_price}</td>
                      <td className="p-4"><StatusBadge status={h.status} /></td>
                      <td className="p-4 text-right whitespace-nowrap">
                        {h.status === "active" && (
                          <button data-testid={`return-hire-${h.id}`} onClick={() => doReturn(h.id)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#001A4D] hover:bg-[#00287A]"><RotateCcw className="w-3 h-3" /> Return</button>
                        )}
                        <button data-testid={`delete-hire-${h.id}`} onClick={() => removeHire(h.id)} className="p-2 hover:text-red-400 text-[#F5F5F0]/50"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {hires.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#F5F5F0]/40">No hires yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bookings */}
        {tab === "bookings" && <AdminBookings />}
        {tab === "qr" && <QRCodes bikes={bikes} />}
        {tab === "guide" && <StaffGuide />}
        {tab === "security" && <SecurityPanel />}
        {tab === "maps" && <MapsEditor />}

        {/* Self-guided */}
        {tab === "self-guided" && (
          <div data-testid="admin-self-guided">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl">Self-Guided Routes</h2>
              <Button data-testid="add-route-btn" onClick={openNewSG} className="rounded-full bg-[#001A4D] hover:bg-[#00287A]"><Plus className="w-4 h-4 mr-1" /> New route</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {routes.map((r) => (
                <div key={r.id} className="border border-white/10 rounded-xl overflow-hidden" data-testid={`admin-route-card-${r.id}`}>
                  <div className="h-36 overflow-hidden bg-white/5">
                    {r.cover_image ? <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Compass className="w-8 h-8 text-white/20" /></div>}
                  </div>
                  <div className="p-5">
                    <p className="overline text-[#F5F5F0]/40">{r.difficulty} · {r.stops?.length || 0} stop{(r.stops?.length || 0) === 1 ? "" : "s"} · ${r.price} + hire</p>
                    <h3 className="font-serif text-2xl mt-1">{r.title}</h3>
                    <button
                      data-testid={`route-code-${r.id}`}
                      onClick={() => copyCode(r.unlock_code)}
                      className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#001A4D]/40 border border-white/10 font-mono text-sm tracking-widest hover:bg-[#001A4D]/60 transition-colors"
                    >
                      <QrCode className="w-4 h-4" /> {r.unlock_code}
                    </button>
                    <div className="mt-4 flex gap-2">
                      <Button data-testid={`voiceovers-${r.id}`} variant="outline" size="sm" onClick={() => { setVoRoute(r); setVoDialog(true); }} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"><Mic className="w-3 h-3 mr-1" /> Voiceovers</Button>
                      <Button data-testid={`manage-codes-${r.id}`} variant="outline" size="sm" onClick={() => openCodes(r)} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"><KeyRound className="w-3 h-3 mr-1" /> Codes</Button>
                      <Button data-testid={`delete-route-${r.id}`} variant="outline" size="sm" onClick={() => removeSG(r.id)} className="rounded-full border-white/20 bg-transparent text-white hover:bg-red-500/20 hover:border-red-400/40"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                    </div>
                  </div>
                </div>
              ))}
              {routes.length === 0 && <p className="text-[#F5F5F0]/40">No self-guided routes yet.</p>}
            </div>
          </div>
        )}
      </div>

      {/* Bike dialog */}
      <Dialog open={bikeDialog} onOpenChange={setBikeDialog}>
        <DialogContent className="bg-[#0A0A0A] text-[#F5F5F0] border-white/10 max-w-lg" data-testid="bike-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editBikeId ? "Edit bike" : "Add bike"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input data-testid="bike-name" className="bg-white/5 border-white/10" value={bikeForm.name} onChange={(e) => setBikeForm({ ...bikeForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price per hour ($)</Label><Input data-testid="bike-price" type="number" className="bg-white/5 border-white/10" value={bikeForm.price_per_hour} onChange={(e) => setBikeForm({ ...bikeForm, price_per_hour: e.target.value })} /></div>
              <div><Label>Total quantity</Label><Input data-testid="bike-qty" type="number" className="bg-white/5 border-white/10" value={bikeForm.total_qty} onChange={(e) => setBikeForm({ ...bikeForm, total_qty: e.target.value })} /></div>
            </div>
            <div><Label>Image URL</Label><Input data-testid="bike-image" className="bg-white/5 border-white/10" value={bikeForm.image} onChange={(e) => setBikeForm({ ...bikeForm, image: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea data-testid="bike-desc" className="bg-white/5 border-white/10" value={bikeForm.description} onChange={(e) => setBikeForm({ ...bikeForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="save-bike" onClick={saveBike} className="rounded-full bg-[#001A4D] hover:bg-[#00287A] w-full">Save bike</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Self-guided route dialog */}
      <Dialog open={sgDialog} onOpenChange={setSgDialog}>
        <DialogContent className="bg-[#0A0A0A] text-[#F5F5F0] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="route-form-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New self-guided route</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="border border-white/10 rounded-lg p-3 bg-[#001A4D]/15">
              <Label className="flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Import from Google Earth (KML)</Label>
              <Input data-testid="kml-import-input" type="file" accept=".kml,.xml" className="bg-white/5 border-white/10 mt-1 file:text-white/70" onChange={(e) => e.target.files?.[0] && importKml(e.target.files[0])} />
              <p className="text-xs text-[#F5F5F0]/40 mt-1">Fills in stops from your mapped pins. You can tweak them below.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Title</Label><Input data-testid="sg-title" className="bg-white/5 border-white/10" value={sgForm.title} onChange={(e) => setSgForm({ ...sgForm, title: e.target.value })} /></div>
              <div><Label>Unlock code</Label><Input data-testid="sg-code" className="bg-white/5 border-white/10 uppercase font-mono tracking-widest" value={sgForm.unlock_code} onChange={(e) => setSgForm({ ...sgForm, unlock_code: e.target.value.toUpperCase() })} /></div>
            </div>
            <div><Label>Description</Label><Textarea data-testid="sg-desc" className="bg-white/5 border-white/10" value={sgForm.description} onChange={(e) => setSgForm({ ...sgForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label>Price $</Label><Input data-testid="sg-price" type="number" className="bg-white/5 border-white/10" value={sgForm.price} onChange={(e) => setSgForm({ ...sgForm, price: e.target.value })} /></div>
              <div><Label>Distance km</Label><Input data-testid="sg-distance" type="number" className="bg-white/5 border-white/10" value={sgForm.distance_km} onChange={(e) => setSgForm({ ...sgForm, distance_km: e.target.value })} /></div>
              <div><Label>Duration</Label><Input data-testid="sg-duration" className="bg-white/5 border-white/10" value={sgForm.duration_text} onChange={(e) => setSgForm({ ...sgForm, duration_text: e.target.value })} placeholder="2 hours" /></div>
              <div><Label>Difficulty</Label>
                <Select value={sgForm.difficulty} onValueChange={(v) => setSgForm({ ...sgForm, difficulty: v })}>
                  <SelectTrigger data-testid="sg-difficulty" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIFFICULTY.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Cover image URL</Label><Input data-testid="sg-cover" className="bg-white/5 border-white/10" value={sgForm.cover_image} onChange={(e) => setSgForm({ ...sgForm, cover_image: e.target.value })} /></div>

            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="overline text-[#F5F5F0]/50">Stops</p>
                <Button data-testid="add-stop-btn" type="button" size="sm" variant="outline" onClick={addStop} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"><Plus className="w-3 h-3 mr-1" /> Add stop</Button>
              </div>
              <div className="space-y-4">
                {sgForm.stops.map((s, i) => (
                  <div key={i} className="border border-white/10 rounded-lg p-4" data-testid={`sg-stop-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs text-[#F5F5F0]/50">Stop {i + 1}</span>
                      {sgForm.stops.length > 1 && <button type="button" onClick={() => removeStop(i)} className="text-[#F5F5F0]/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                    <Input data-testid={`sg-stop-name-${i}`} className="bg-white/5 border-white/10 mb-2" placeholder="Stop name" value={s.name} onChange={(e) => setStop(i, "name", e.target.value)} />
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Input data-testid={`sg-stop-lat-${i}`} className="bg-white/5 border-white/10" placeholder="Latitude" value={s.lat} onChange={(e) => setStop(i, "lat", e.target.value)} />
                      <Input data-testid={`sg-stop-lng-${i}`} className="bg-white/5 border-white/10" placeholder="Longitude" value={s.lng} onChange={(e) => setStop(i, "lng", e.target.value)} />
                    </div>
                    <Textarea data-testid={`sg-stop-images-${i}`} className="bg-white/5 border-white/10 mb-2" placeholder="Image URLs — one per line (gallery)" value={s.imagesText} onChange={(e) => setStop(i, "imagesText", e.target.value)} />
                    <Textarea data-testid={`sg-stop-story-${i}`} className="bg-white/5 border-white/10" placeholder="The story that gets narrated at this stop..." value={s.story} onChange={(e) => setStop(i, "story", e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-route" onClick={saveSG} className="rounded-full bg-[#001A4D] hover:bg-[#00287A] w-full">Save route</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access codes dialog */}
      {/* Access codes dialog */}
      <Dialog open={voDialog} onOpenChange={setVoDialog}>
        <DialogContent className="bg-[#0A0A0A] text-[#F5F5F0] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="voiceover-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Voiceovers — {voRoute?.title}</DialogTitle></DialogHeader>
          <VoiceoverManager route={voRoute} onChange={() => { getSelfGuidedRoutes().then((rs) => { setRoutes(rs); setVoRoute((vr) => rs.find((x) => x.id === vr?.id) || vr); }).catch(() => {}); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={codesDialog} onOpenChange={setCodesDialog}>
        <DialogContent className="bg-[#0A0A0A] text-[#F5F5F0] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="codes-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Access codes — {codesRoute?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-[#001A4D]/20 border border-white/10 rounded-lg px-4 py-3">
              <div>
                <p className="overline text-[#F5F5F0]/40">Shared code · reusable · 12h per device</p>
                <p className="font-mono text-lg tracking-widest">{codesRoute?.unlock_code}</p>
              </div>
              <button data-testid="copy-shared-code" onClick={() => copyCode(codesRoute?.unlock_code)} className="p-2 text-[#F5F5F0]/60 hover:text-white"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label>Generate unique codes</Label>
                <Input data-testid="gen-count" type="number" min="1" max="100" className="bg-white/5 border-white/10" value={genCount} onChange={(e) => setGenCount(e.target.value)} />
              </div>
              <Button data-testid="generate-codes-btn" onClick={doGenerate} disabled={genLoading} className="rounded-full bg-[#001A4D] hover:bg-[#00287A]"><Plus className="w-4 h-4 mr-1" /> Generate</Button>
              <Button data-testid="print-all-codes-btn" variant="outline" onClick={() => printCards(codesList.map((c) => c.code))} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"><Printer className="w-4 h-4 mr-1" /> Print all</Button>
            </div>
            <p className="text-xs text-[#F5F5F0]/40">Unique codes activate on first use, then lock to that device for 12 hours.</p>
            {codesList.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="code-usage-summary">
                <div className="border border-white/10 rounded-lg p-3 text-center"><p className="font-serif text-2xl">{codesList.filter((c) => c.status === "active").length}</p><p className="overline text-[#F5F5F0]/40">Active</p></div>
                <div className="border border-white/10 rounded-lg p-3 text-center"><p className="font-serif text-2xl">{codesList.filter((c) => c.activated).length}</p><p className="overline text-[#F5F5F0]/40">Activated</p></div>
                <div className="border border-white/10 rounded-lg p-3 text-center"><p className="font-serif text-2xl">{codesList.filter((c) => c.activated_at && new Date(c.activated_at).toDateString() === new Date().toDateString()).length}</p><p className="overline text-[#F5F5F0]/40">Today</p></div>
              </div>
            )}
            <div className="space-y-2">
              <p className="overline text-[#F5F5F0]/40">Unique codes ({codesList.length})</p>
              {codesList.length === 0 && <p className="text-sm text-[#F5F5F0]/40">No unique codes yet. Generate a batch to hand out in store.</p>}
              {codesList.map((c) => (
                <div key={c.id} data-testid={`code-row-${c.code}`} className="flex items-center justify-between border border-white/10 rounded-lg px-3 py-2">
                  <div>
                    <span className="font-mono tracking-widest">{c.code}</span>
                    {c.booking_ref && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#2563EB]/20 text-[#93C5FD] font-mono" data-testid={`code-booking-${c.code}`}>{c.booking_ref}{c.role === "extra" ? " · secondary" : " · master"}</span>}
                    {c.activated_at && <span className="block text-xs text-[#F5F5F0]/40 font-mono">Activated {new Date(c.activated_at).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'unused' ? 'bg-emerald-500/20 text-emerald-300' : c.status === 'active' ? 'bg-amber-500/20 text-amber-200' : 'bg-white/10 text-white/40'}`}>{c.status}</span>
                    {!c.booking_id && <button data-testid={`attach-code-${c.code}`} title="Attach to booking" onClick={() => setAttach({ code: c.code, title: codesRoute?.title })} className="p-1.5 text-[#F5F5F0]/50 hover:text-white"><Link2 className="w-4 h-4" /></button>}
                    <button data-testid={`print-code-${c.code}`} onClick={() => printCards([c.code])} className="p-1.5 text-[#F5F5F0]/50 hover:text-white"><Printer className="w-4 h-4" /></button>
                    <button data-testid={`copy-code-${c.code}`} onClick={() => copyCode(c.code)} className="p-1.5 text-[#F5F5F0]/50 hover:text-white"><Copy className="w-4 h-4" /></button>
                    <button data-testid={`delete-code-${c.code}`} onClick={() => removeCode(c.id)} className="p-1.5 text-[#F5F5F0]/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AttachCodeDialog code={attach?.code} routeTitle={attach?.title} onClose={() => setAttach(null)} onDone={() => codesRoute && listCodes(codesRoute.id).then(setCodesList).catch(() => {})} />
    </div>
  );
}

// Staff gate: customers hitting /admin see the login screen only
export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => { staffMe().then(setUser).catch(() => setUser(false)); }, []);
  if (user === null) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-checking" />;
  if (!user) return <StaffLogin onSuccess={setUser} />;
  return <AdminInner user={user} onLogout={async () => { await staffLogout(); setUser(false); }} />;
}
